import type { RadioButtonChangeEvent } from "primereact/radiobutton";
import { useContext, useId } from "react";

import { useStableRest } from "../../utils/use-stable-rest";
import { RadioGroupContext } from "../radio-group/radio-group.context";
import type { SelectableCardProps, SelectableCardViewModel } from "./types-selectable-card";

/** ViewModel do SelectableCard: estado do cartão (próprio ou do `RadioGroup`), `id` do input e o handler de seleção; sem JSX. */
export function useSelectableCard<T = string>({
  value,
  selected,
  disabled,
  control = "radio",
  name,
  onSelect,
  id,
  inputTestId,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  className,
  style,
  children,
  ...props
}: SelectableCardProps<T>): SelectableCardViewModel<T> {
  const rest = useStableRest(props);
  const group = useContext(RadioGroupContext);
  const generatedId = useId();
  const isDisabled = Boolean(disabled || group?.disabled);
  const isSelected = selected ?? (group ? group.value === value : false);

  function select() {
    if (isDisabled) return;
    group?.select(value);
    onSelect?.(value);
  }

  return {
    rest,
    className,
    style,
    children,
    control,
    disabled: isDisabled,
    selected: isSelected,
    inputProps: {
      id: id ?? generatedId,
      name: name ?? group?.name,
      value: String(value),
      checked: isSelected,
      disabled: isDisabled,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "aria-describedby": ariaDescribedBy,
      "data-testid": inputTestId,
    },
    radioProps: {
      inputId: id ?? generatedId,
      name: name ?? group?.name,
      value,
      checked: isSelected,
      disabled: isDisabled,
      invalid: group?.invalid,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "aria-describedby": ariaDescribedBy,
      onChange: (_event: RadioButtonChangeEvent) => select(),
    },
    onInputChange: select,
  };
}
